import {
  HeaderContainer,
  HeaderLeftSection,
  HeaderRightSection,
  HeaderContent,
  Logo,
  MenuButton,
  IconButton,
  NotificationBadge,
  CurrencySelector,
  ProfileButton,
  UserMenuContainer,
} from './Header.styles';
import { Icon } from '@tviklax/ui-components';
import React, { useState } from 'react';
import styled from 'styled-components';
import ProfileDropdown from './ProfileDropdown';
import { AnimatePresence } from 'framer-motion';
import { useRole } from '../../context/RoleContext';
import { useUser } from '../../context/UserContext';
import logoFull from '../../assets/logo.png';

interface HeaderProps {
  onMenuToggle: () => void;
  onLogout: () => void;
  onNavigate: (tab: string) => void;
  userName?: string;
  userRole?: string;
  onOpenMessagingDrawer?: () => void;
}

const ContextStrip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const PrimaryLine = styled.div`
  font-size: ${({ theme }) => theme.typography?.fontSize?.sm || '14px'};
  font-weight: ${({ theme }) => theme.typography?.fontWeight?.semibold || 600};
  color: ${({ theme }) => theme.colors?.textPrimary || '#111827'};
  line-height: 1.3;
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography?.fontSize?.xs || '12px'};
  font-weight: ${({ theme }) => theme.typography?.fontWeight?.regular || 400};
  color: ${({ theme }) => theme.colors?.textSecondary || '#6b7280'};
  line-height: 1.3;
  flex-wrap: wrap;
`;

export const Header: React.FC<HeaderProps> = ({
  onMenuToggle,
  onLogout,
  onNavigate,
  userName,
  userRole,
  onOpenMessagingDrawer,
}) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { roleLabel } = useRole();
  const { user } = useUser();

  // Use actual user data from context if available, otherwise use props
  const displayName = user?.name || userName || 'User';
  const displayRole = user?.role || userRole || roleLabel;
  const profileAvatarSrc = user?.profilePicture?.startsWith('http')
    ? user.profilePicture
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=e2e8f0&color=1e293b&size=256`;

  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <HeaderLeftSection>
          <MenuButton onClick={onMenuToggle} aria-label="Toggle menu">
            <Icon name="Menu" size={24} />
          </MenuButton>
          <Logo>
            <img src={logoFull} alt="Tvikla Logo" style={{ height: '32px' }} />
          </Logo>

          {/* <QuickLinksContainer>
            <QuickLinksButton
              $isOpen={isQuickLinksOpen}
              onClick={toggleQuickLinks}
            >
              <QuickLinksText>Quick Links</QuickLinksText>
              <Icon name="ChevronDown" size={16} />
            </QuickLinksButton>
            <AnimatePresence>
              {isQuickLinksOpen && <QuickLinksDropdown />}
            </AnimatePresence>
          </QuickLinksContainer> */}
        </HeaderLeftSection>

        <HeaderRightSection>
          {displayName && (
            <ContextStrip>
              <PrimaryLine>Welcome back, {displayName}</PrimaryLine>
              {displayRole && (
                <MetaLine>
                  <span>{displayRole}</span>
                </MetaLine>
              )}
            </ContextStrip>
          )}

          <CurrencySelector>
            <span style={{ fontSize: '1.2rem', display: 'flex' }}>🇺🇸</span> <Icon name="ChevronDown" size={14} />
          </CurrencySelector>

          <IconButton>
            <Icon name="Bell" size={20} />
            <NotificationBadge />
          </IconButton>

          <IconButton onClick={onOpenMessagingDrawer} aria-label="Open messaging drawer">
            <Icon name="Mail" size={20} />
          </IconButton>

          <UserMenuContainer>
            <ProfileButton onClick={toggleProfile}>
              <img
                src={profileAvatarSrc}
                alt="User Profile"
              />
            </ProfileButton>
            <AnimatePresence>
              {isProfileOpen && (
                <ProfileDropdown
                  avatarSrc={profileAvatarSrc}
                  onLogout={onLogout}
                  onNavigate={(tab) => {
                    setIsProfileOpen(false);
                    onNavigate(tab);
                  }}
                />
              )}
            </AnimatePresence>
          </UserMenuContainer>
        </HeaderRightSection>
      </HeaderContent>
    </HeaderContainer>
  );
};
